// TelcoTrack — společné typy / konstanty skladových pohybů (audit object_history)

export const WAREHOUSE_MOVEMENT_CHANGE_TYPES = [
  'warehouse_receipt',
  'warehouse_issue',
  'warehouse_transfer',
  'warehouse_return',
  'warehouse_inventory',
] as const;

export type WarehouseMovementChangeType = (typeof WAREHOUSE_MOVEMENT_CHANGE_TYPES)[number];

export interface WarehouseMovementLine {
  sapCode?: string | null;
  productId?: string | null;
  productName?: string | null;
  quantity: number;
  unit?: string | null;
  /** Jednotková cena spotřeby / příjmu (Kč). */
  unitPrice?: number | null;
  /** lineTotal = qty × unitPrice, pokud známé. */
  lineTotal?: number | null;
  warehouseItemId?: string | null;
  batchNumber?: string | null;
}

export interface WarehouseMovementDto {
  id: string;
  changeType: WarehouseMovementChangeType | string;
  objectId: string;
  objectType: string | null;
  description: string | null;
  performedBy: string;
  performedAt: string;
  warehouseId?: string | null;
  warehouseFromId?: string | null;
  warehouseToId?: string | null;
  items: WarehouseMovementLine[];
  /** Součet lineTotal (pokud ceny známé). */
  totalPrice?: number | null;
}

/** Řádek TT příjemky pro overlay ceny na auditovaný pohyb kusu. */
export interface WarehouseReceiptLineForMovement {
  receiptId: string;
  sapCode?: string | null;
  productId?: string | null;
  productName?: string | null;
  quantity: number;
  unit?: string | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
  warehouseItemId?: string | null;
  batchNumber?: string | null;
}

/** Popisek pohybu pro UI (CS). */
export function warehouseMovementLabelCs(changeType: string): string {
  switch (changeType) {
    case 'warehouse_receipt':
    case 'warehouse_goods_receipt':
      return 'Příjem';
    case 'warehouse_issue':
    case 'warehouse_goods_issue':
      return 'Výdej';
    case 'warehouse_transfer':
      return 'Převod';
    case 'warehouse_return':
      return 'Vratka';
    case 'warehouse_inventory':
      return 'Inventura';
    default:
      return changeType;
  }
}

export function isWarehouseReceiptMovement(changeType: string | null | undefined): boolean {
  const t = String(changeType ?? '');
  return t === 'warehouse_receipt' || t === 'warehouse_goods_receipt';
}

/** JSONB / numeric string → kladná cena, jinak null. */
export function finitePositivePrice(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function normalizeWarehouseMovementLine(line: WarehouseMovementLine): WarehouseMovementLine {
  const qty = Number(line.quantity);
  return {
    ...line,
    quantity: Number.isFinite(qty) ? qty : 0,
    unitPrice: finitePositivePrice(line.unitPrice),
    lineTotal: finitePositivePrice(line.lineTotal),
  };
}

/** Vážená jednotková cena z řádků pohybu (Kč / MJ). */
export function receiptMovementUnitPrice(
  lines: Array<{ unitPrice?: number | null; quantity?: number | null }>,
): number | null {
  let cost = 0;
  let qtyPriced = 0;
  let fallback: number | null = null;
  for (const line of lines) {
    const unit = finitePositivePrice(line.unitPrice);
    if (unit == null) continue;
    if (fallback == null) fallback = unit;
    const qty = Number(line.quantity);
    if (!Number.isFinite(qty) || qty <= 0) continue;
    cost += unit * qty;
    qtyPriced += qty;
  }
  if (qtyPriced <= 0) return fallback;
  return roundMoney(cost / qtyPriced);
}

/** Součet lineTotal z řádků (ne z totálu celého dokladu). */
export function receiptMovementLineTotal(
  lines: Array<{ unitPrice?: number | null; quantity?: number | null; lineTotal?: number | null }>,
): number | null {
  let sum = 0;
  let any = false;
  for (const line of lines) {
    const explicit = finitePositivePrice(line.lineTotal);
    if (explicit != null) {
      sum += explicit;
      any = true;
      continue;
    }
    const unit = finitePositivePrice(line.unitPrice);
    const qty = Number(line.quantity);
    if (unit == null || !Number.isFinite(qty) || qty <= 0) continue;
    sum += roundMoney(unit * qty);
    any = true;
  }
  if (!any) return null;
  return roundMoney(sum);
}

/**
 * Doplní unitPrice / lineTotal z TT příjemky na příjem-pohyby kusu.
 * Audit JSON někdy cenu nebo warehouseItemId nemá — příjemka je SSOT.
 */
export function overlayReceiptPricesOnMovements(
  movements: WarehouseMovementDto[],
  receiptLines: WarehouseReceiptLineForMovement[],
  warehouseItemId: string,
): WarehouseMovementDto[] {
  const itemId = (warehouseItemId ?? '').trim();
  if (!itemId || !receiptLines.length) {
    return movements.map((m) => ({
      ...m,
      items: (m.items ?? []).map(normalizeWarehouseMovementLine),
    }));
  }

  const byReceipt = new Map<string, WarehouseReceiptLineForMovement[]>();
  for (const line of receiptLines) {
    const rid = String(line.receiptId ?? '').trim();
    if (!rid) continue;
    const wi = String(line.warehouseItemId ?? '').trim();
    if (wi && wi !== itemId) continue;
    const arr = byReceipt.get(rid) ?? [];
    arr.push(line);
    byReceipt.set(rid, arr);
  }

  return movements.map((m) => {
    const items = (m.items ?? []).map(normalizeWarehouseMovementLine);
    if (!isWarehouseReceiptMovement(m.changeType)) {
      return { ...m, items };
    }
    const fromReceipt = byReceipt.get(String(m.objectId ?? '').trim()) ?? [];
    if (!fromReceipt.length) {
      return { ...m, items };
    }

    const priced =
      items.length > 0
        ? items.map((item) => mergeReceiptPriceOntoLine(item, fromReceipt, itemId))
        : fromReceipt.map((row) => receiptLineToMovementLine(row, itemId));
    const totalFromLines = receiptMovementLineTotal(priced);
    return {
      ...m,
      items: priced,
      totalPrice: totalFromLines ?? m.totalPrice ?? null,
    };
  });
}

function receiptLineToMovementLine(
  row: WarehouseReceiptLineForMovement,
  warehouseItemId: string,
): WarehouseMovementLine {
  return normalizeWarehouseMovementLine({
    sapCode: row.sapCode ?? null,
    productId: row.productId ?? null,
    productName: row.productName ?? null,
    quantity: Number(row.quantity) || 0,
    unit: row.unit ?? null,
    unitPrice: row.unitPrice ?? null,
    lineTotal: row.lineTotal ?? null,
    warehouseItemId,
  });
}

function mergeReceiptPriceOntoLine(
  item: WarehouseMovementLine,
  fromReceipt: WarehouseReceiptLineForMovement[],
  itemId: string,
): WarehouseMovementLine {
  const match =
    fromReceipt.find((r) => String(r.warehouseItemId ?? '').trim() === itemId) ??
    fromReceipt.find(
      (r) =>
        String(r.sapCode ?? '').trim() &&
        String(r.sapCode ?? '').trim() === String(item.sapCode ?? '').trim(),
    ) ??
    fromReceipt[0];
  if (!match) return { ...item, warehouseItemId: item.warehouseItemId || itemId };
  return normalizeWarehouseMovementLine({
    ...item,
    warehouseItemId: item.warehouseItemId || itemId,
    unitPrice: finitePositivePrice(item.unitPrice) ?? match.unitPrice ?? null,
    lineTotal: finitePositivePrice(item.lineTotal) ?? match.lineTotal ?? null,
    quantity: Number(item.quantity) > 0 ? item.quantity : Number(match.quantity) || 0,
    unit: item.unit ?? match.unit ?? null,
    sapCode: item.sapCode ?? match.sapCode ?? null,
    productName: item.productName ?? match.productName ?? null,
  });
}
